import React, { useState } from 'react';
import { Property } from '../../types/property';
import { CashFlowBreakdown } from './CashFlowBreakdown';
import { BRRRRAnalysis } from './BRRRRAnalysis';
import { formatCurrency } from '../../utils/formatting';

interface AnalysisAccordionProps {
  property: Property;
  onPropertyUpdate: (propertyId: number, updates: Partial<Property>) => void;
  expandedSections?: string[];
  onToggleSection?: (section: string) => void;
}

type Section = 'cash_flow' | 'brrrr';

export const AnalysisAccordion: React.FC<AnalysisAccordionProps> = ({ property, onPropertyUpdate, expandedSections: expandedSectionsArr, onToggleSection }) => {
  const [localExpanded, setLocalExpanded] = useState<Set<Section>>(new Set());
  const expandedSections = expandedSectionsArr != null ? new Set(expandedSectionsArr) : localExpanded;
  const toggleSection = (section: Section) => {
    if (onToggleSection) {
      onToggleSection(section);
    } else {
      setLocalExpanded(prev => {
        const next = new Set(prev);
        if (next.has(section)) next.delete(section); else next.add(section);
        return next;
      });
    }
  };

  // Check if sections have data
  const hasCashFlow = property.section8_rent && property.expense_breakdown && property.monthly_mortgage;
  const hasBRRRR = !!property.brrrr;
  if (!hasCashFlow && !hasBRRRR) {
    return null;
  }

  return (
    <div className="mt-4 pt-4 border-t border-gray-200 space-y-2">

      {/* ── CASH FLOW SECTION ── */}
      {hasCashFlow && (
        <div className={`rounded-xl overflow-hidden border transition-all ${expandedSections.has('cash_flow') ? 'border-green-400 shadow-sm' : 'border-gray-200'}`}>
          <button
            onClick={() => toggleSection('cash_flow')}
            className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${expandedSections.has('cash_flow') ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-50 hover:bg-green-50'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${expandedSections.has('cash_flow') ? 'bg-white/20' : 'bg-green-100'}`}>
                <span className="text-base">💰</span>
              </div>
              <div className="text-left">
                <h4 className={`text-sm font-bold ${expandedSections.has('cash_flow') ? 'text-white' : 'text-gray-800'}`}>Cash Flow Analysis</h4>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transition-transform flex-shrink-0 ${expandedSections.has('cash_flow') ? 'rotate-180 text-white' : 'text-gray-400'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.has('cash_flow') && (
            <div className="px-4 pb-4 bg-white">
              <CashFlowBreakdown property={property} />
            </div>
          )}
        </div>
      )}

      {/* ── BRRRR SECTION ── */}
      {hasBRRRR && (
        <div className={`rounded-xl overflow-hidden border transition-all ${expandedSections.has('brrrr') ? 'border-blue-400 shadow-sm' : 'border-gray-200'}`}>
          <button
            onClick={() => toggleSection('brrrr')}
            className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${expandedSections.has('brrrr') ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-50 hover:bg-blue-50'}`}
          >
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${expandedSections.has('brrrr') ? 'bg-white/20' : 'bg-blue-100'}`}>
                <span className="text-base">🔄</span>
              </div>
              <div className="text-left">
                <h4 className={`text-sm font-bold ${expandedSections.has('brrrr') ? 'text-white' : 'text-gray-800'}`}>BRRRR Strategy</h4>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transition-transform flex-shrink-0 ${expandedSections.has('brrrr') ? 'rotate-180 text-white' : 'text-gray-400'}`}
              fill="none" stroke="currentColor" viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {expandedSections.has('brrrr') && property.brrrr && (
            <div className="px-4 pb-4 bg-white">
              <BRRRRAnalysis brrrr={property.brrrr} property={property} onPropertyUpdate={onPropertyUpdate} />
            </div>
          )}
        </div>
      )}
    </div>
  );
};
